<script setup>
import { computed, ref, watch, onMounted, onUnmounted, onDeactivated } from 'vue';
import DefaultLayout from '@/layouts/default.vue';
import Card from 'primevue/card';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Paginator from 'primevue/paginator';
import Checkbox from 'primevue/checkbox';
import { useSearchStore } from '@/stores/search.js';

const store = useSearchStore();

// --- Конфигурация вкладок ---
const TYPES = ['customers', 'facilities', 'contracts', 'works', 'contacts'];
const TITLES = {
  customers: 'Клиенты',
  facilities: 'Объекты',
  contracts: 'Договоры',
  works: 'Работы',
  contacts: 'Контакты',
};

// --- Локальное состояние ---
const activeIdx = ref(0);
const firstDataApplied = ref(false);

// --- Вычисляемые свойства (прокси к стору) ---
const query = computed(() => store.query);
const loading = computed(() => store.loading);
const totals = computed(() => store.totals);

const includeExtended = computed({
  get: () => store.includeExtended,
  set: (v) => store.setIncludeExtended(v),
});
const selectedTypes = computed({
  get: () => store.typesSelected,
  set: (arr) => store.setTypes(arr),
});

// --- Хелперы и обработчики ---
function facet(type) {
  return store.facets[type];
}

function onFacetPage(type, e) {
  store.setPage(type, e.page + 1);
  store.fetchFacet(type);
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

function highlight(text, q) {
  const t = escapeHtml(String(text || ''));
  if (!q || q.length < 2) return t;
  const esc = escapeHtml(q).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return t.replace(new RegExp(esc, 'gi'), (m) => `<mark>${m}</mark>`);
}


// Однократно выбираем первую непустую вкладку
watch(
    totals,
    () => {
      if (firstDataApplied.value) return;
      const idx = TYPES.findIndex((t) => (totals.value?.[t] || 0) > 0);
      activeIdx.value = idx >= 0 ? idx : 0;
      firstDataApplied.value = true;
    },
    { immediate: false }
);

// Перезагружаем все данные при изменении параметров поиска
watch(() => store.query, () => {
  firstDataApplied.value = false;
  store.fetchAll();
});
watch(includeExtended, () => {
  firstDataApplied.value = false;
  store.fetchAll();
});
watch(selectedTypes, () => {
  firstDataApplied.value = false;
  store.fetchAll();
});

// Первая загрузка данных при монтировании, если запрос уже есть
onMounted(() => {
  if (store.query?.trim()) {
    firstDataApplied.value = false;
    store.fetchAll();
  }
});

// **Ключевое исправление:** Очистка стора при уходе со страницы
const cleanup = () => {
  if (typeof store.clearResults === 'function') {
    store.clearResults();
  }
};
onUnmounted(cleanup);
onDeactivated(cleanup); // для <keep-alive>
</script>

<template>
  <DefaultLayout>
    <Card
        class="w-full flex-1 flex flex-col min-h-0"
        :pt="{ content: { class: 'p-4 flex-1 flex flex-col min-h-0 gap-3 h-full' } }"
    >
      <template #content>
        <!-- Строка с запросом -->
        <div class="text-lg font-semibold">Запрос: "{{ query }}"</div>

        <!-- Панель параметров -->
        <div class="flex items-center gap-6 flex-wrap">
          <div class="flex items-center gap-2">
            <Checkbox v-model="includeExtended" binary inputId="ext" />
            <label for="ext" class="text-sm">Расширенный поиск</label>
          </div>

          <div class="flex items-center gap-3">
            <span class="text-sm text-gray-500">Искать в:</span>
            <div class="flex gap-3 flex-wrap">
              <label v-for="t in TYPES" :key="t" class="flex items-center gap-1 text-sm">
                <Checkbox
                    :modelValue="selectedTypes.includes(t)"
                    @update:modelValue="(v)=>{
                      const set = new Set(selectedTypes);
                      v ? set.add(t) : set.delete(t);
                      selectedTypes = Array.from(set);
                    }"
                    binary
                />
                <span>{{ TITLES[t] }}</span>
              </label>
            </div>
          </div>

          <div class="text-sm text-gray-500 ml-auto">
            Всего: {{ store.totalAll }}
          </div>
        </div>

        <!-- Вкладки -->
        <Tabs v-model:value="activeIdx" class="flex-1 min-h-0 flex flex-col h-full">
          <TabList>
            <Tab
                v-for="(t, i) in TYPES"
                :key="t"
                :value="i"
                :disabled="(totals?.[t] || 0) === 0"
            >
              <div class="flex items-center gap-2">
                <span>{{ TITLES[t] }}</span>
                <span class="text-gray-500">({{ totals?.[t] || 0 }})</span>
              </div>
            </Tab>
          </TabList>

          <TabPanels class="flex-1 min-h-0 flex flex-col">
            <TabPanel
                v-for="(t, i) in TYPES"
                :key="`panel:${t}`"
                :value="i"
                class="flex-1 min-h-0 flex flex-col"
            >
              <div v-if="loading" class="p-4 text-center text-gray-500 flex-1 flex items-center justify-center">
                Загрузка...
              </div>

              <template v-else>
                <div class="flex-1 min-h-0 overflow-auto">
                  <div v-if="(facet(t).items || []).length === 0" class="p-4 text-center text-gray-400">
                    Нет результатов
                  </div>

                  <div
                      v-for="it in facet(t).items"
                      :key="`${t}:${it.id}`"
                      class="p-3 border-b border-gray-100"
                  >
                    <div class="font-semibold break-words overflow-hidden">
                      <router-link
                          v-if="['customers','facilities','contracts','works'].includes(t)"
                          :to="(t==='customers'?'/customers': t==='facilities'?'/facilities': t==='contracts'?'/contracts':'/works') + '/' + it.id"
                          v-html="highlight(it.title, query)"
                          class="hover:underline"
                      />
                      <span v-else v-html="highlight(it.title, query)"></span>
                    </div>

                    <div
                        v-if="it.subtitle"
                        class="text-gray-600 break-words overflow-hidden mt-0.5"
                        v-html="highlight(it.subtitle, query)"
                    />

                    <div class="text-gray-500 mt-1 space-x-3">
                      <template v-if="it.extra && Object.keys(it.extra).length">
                        <span v-for="(val, key) in it.extra" :key="key" class="inline-block">
                          <b>{{ key }}:</b>
                          <span v-html="highlight(String(val || ''), query)"></span>
                        </span>
                      </template>
                      <span v-else class="text-gray-400">—</span>
                    </div>
                  </div>
                </div>
                <div
                    v-if="facet(t).total > facet(t).pageSize"
                    class="mt-2 flex-shrink-0"
                >
                  <Paginator
                      :rows="facet(t).pageSize"
                      :first="(facet(t).page - 1) * facet(t).pageSize"
                      :total-records="facet(t).total"
                      template="PrevPageLink PageLinks NextPageLink"
                      @page="(e)=>onFacetPage(t, e)"
                  />
                </div>
              </template>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </template>
    </Card>
  </DefaultLayout>
</template>

<style scoped>
:deep(.p-card) { display: flex; flex-direction: column; min-height: 0; }
:deep(.p-card-body) { display: flex; flex-direction: column; flex: 1; min-height: 0; }
:deep(.p-card-content) { display: flex; flex-direction: column; flex: 1; min-height: 0; }

.break-words {
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>